import { useState } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { Message, useMessage } from '@messageformat/react';
import { Col, Form, FormRow, Row, Tile } from '@op/opds.opux';
import { Stepper } from '@op/opds.opux-stepper';
import { font, space } from '@op/opds.tokens';
import { getChartOptionsWithOverrides } from '../../util/defaultOptions';
import { NoOpFormSubmitHandler } from '../../util/formUtils';

const BankrupcyGraph = (): JSX.Element => {
  const [stockCount, setStockCount] = useState(3);

  const beginningTitle = useMessage('course2.bankrupcy_graph.beginning');
  const afterTitle = useMessage('course2.bankrupcy_graph.after');
  const stepperLabel = useMessage('course2.bankrupcy_graph.stock_amount');

  const stocks = useMessage('course2.bankrupcy_graph.stocks');
  const stockPortfolio = useMessage('course2.bankrupcy_graph.stock_portfolio');

  const generateOptions = (
    title: string,
    seriesData: { name: string; data: number[]; color: string; pointWidth: number }[],
  ): Record<string, any> => {
    return getChartOptionsWithOverrides({
      accessibility: {
        enabled: false,
      },
      chart: {
        type: 'column',
        marginTop: 40,
        height: 200,
        width: null,
      },
      title: {
        text: title,
      },
      plotOptions: {
        series: {
          enableMouseTracking: false,
          marker: { enabled: false },
        },
      },
      yAxis: {
        max: 100,
        min: 0,
        tickInterval: 20,
        showLastLabel: true,
        showFirstLabel: true,
        opposite: false,
      },
      xAxis: {
        categories: Array(stockCount).fill(stocks).concat(stockPortfolio),
        labels: {
          formatter: () => {
            return '';
          },
        },
      },
      series: seriesData as Highcharts.SeriesOptionsType[],
    });
  };

  const options1 = generateOptions(beginningTitle, [
    {
      name: stocks,
      data: Array(stockCount).fill(100).concat(0),
      color: '#EE5A00',
      pointWidth: 20,
    },
    {
      name: stockPortfolio,
      data: Array(stockCount).fill(0).concat(100),
      color: '#078FE7',
      pointWidth: 40,
    },
  ]);

  const options2 = generateOptions(afterTitle, [
    {
      name: stocks,
      data: Array(stockCount - 1)
        .fill(100)
        .concat([1]),
      color: '#EE5A00',
      pointWidth: 20,
    },
    {
      name: stockPortfolio,
      data: Array(stockCount)
        .fill(0)
        .concat([((stockCount - 1) / stockCount) * 100 || 1]),
      color: '#078FE7',
      pointWidth: 40,
    },
  ]);

  const handleStockCountChange = (value: number): void => {
    if (Number.isNaN(value)) setStockCount(1);
    else setStockCount(Math.max(1, Math.min(value, 10)));
  };

  const valueDecrease = Math.floor((1 - (stockCount - 1) / stockCount) * 100);

  return (
    <div className="investoracademy-graph-wrapper">
      <Tile>
        <h4 className="investoracademy-general-graph-title">
          <Message id="course2.bankrupcy_graph.title" />
        </h4>
        <p className="investoracademy-general-graph-description">
          <Message id="course2.bankrupcy_graph.body" />
        </p>
        <div
          style={{ marginTop: space.small }}
          role="img"
          aria-labelledby="investoracademy-bankcrupcy-graph-description"
          id="investoracademy-bankrupcy-graph-container"
        >
          <Row>
            <Col style={{ display: 'flex' }} xs={12} md={6}>
              <div className="investoracademy-general-graph-container">
                <div>
                  <div>
                    <HighchartsReact highcharts={Highcharts} options={options1} />
                  </div>
                  <div className="investoracademy-bankrupcygraph-label-container">
                    <p
                      className="align-center"
                      style={{ fontSize: font.size.body.small }}
                    >
                      <Message id="course2.bankrupcy_graph.stocks" />
                    </p>
                    <p
                      className="align-center"
                      style={{ fontSize: font.size.body.small }}
                    >
                      <Message id="course2.bankrupcy_graph.stock_portfolio" />
                    </p>
                  </div>
                </div>
              </div>
            </Col>
            <Col style={{ display: 'flex' }} xs={12} md={6}>
              <div className="investoracademy-general-graph-container">
                <div>
                  <HighchartsReact highcharts={Highcharts} options={options2} />
                  <div className="investoracademy-bankrupcygraph-label-container">
                    <p
                      className="align-center"
                      style={{ fontSize: font.size.body.small }}
                    >
                      <Message id="course2.bankrupcy_graph.stocks" />
                    </p>
                    <p
                      className="align-center"
                      style={{ fontSize: font.size.body.small }}
                    >
                      <Message id="course2.bankrupcy_graph.stock_portfolio" />
                    </p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <p id="investoracademy-bankcrupcy-graph-description" className="sr-only">
            {stockCount === 1 ? (
              <Message id="course2.bankrupcy_graph.alt_text_one" />
            ) : (
              <Message
                id="course2.bankrupcy_graph.alt_text_other"
                count={stockCount}
                decrease={valueDecrease}
              />
            )}
          </p>
        </div>
        <div className="investoracademy-vertical-flex-align">
          <div className="investoracademy-bankrupcygraph-stepper-container">
            <Form stacked onSubmit={NoOpFormSubmitHandler}>
              <FormRow label={stepperLabel} required>
                <Stepper
                  min={1}
                  max={10}
                  initialValue={stockCount}
                  onChange={handleStockCountChange}
                  aria-controls="investoracademy-bankrupcy-graph-container"
                />
              </FormRow>
            </Form>
          </div>
        </div>
      </Tile>
    </div>
  );
};

export default BankrupcyGraph;
